import { ReactNode, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Divider,
  Skeleton,
  Typography,
  alpha,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import {
  AccreditationIcon,
  CredentialIcon,
  InfoIcon,
  ProfileIcon,
  RefreshIcon,
} from 'src/v3/core/icons'
import { fetchAccreditationReadiness } from 'src/v3/classroom-v2/utils/api'
import {
  AccreditationReadiness,
  AccreditationReadinessBody,
  AccreditationReadinessEntry,
  VirginiaCreditCategory,
  VirginiaProviderLevel,
} from 'src/v3/classroom-v2/types'

let bodyLabel: Record<AccreditationReadinessBody, string> = {
  CAPCE: 'CAPCE (NREMT)',
  Virginia: 'Virginia',
  CEBrokerTennessee: 'Tennessee (CE Broker)',
  CEBrokerFlorida: 'Florida (CE Broker)',
}

let missingFieldLabel: Record<string, string> = {
  nremtNumber: 'NREMT Number',
  'CEBrokerFlorida:eligibleLicenseType': 'Florida (CE Broker)',
  'CEBrokerTennessee:eligibleLicenseType': 'Tennessee (CE Broker)',
  'Virginia:eligibleLicenseType': 'Virginia',
}

let resolveMissingFieldLabel = (
  body: AccreditationReadinessBody,
  field: string,
): string =>
  missingFieldLabel[`${body}:${field}`] ?? missingFieldLabel[field] ?? field

let missingFieldChipLabel: Record<string, string> = {
  nremtNumber: 'Missing',
  'CEBrokerFlorida:eligibleLicenseType': 'License Type',
  'CEBrokerTennessee:eligibleLicenseType': 'License Type',
  'Virginia:eligibleLicenseType': 'License Type',
}

let resolveMissingFieldChipLabel = (
  body: AccreditationReadinessBody,
  field: string,
): string =>
  missingFieldChipLabel[`${body}:${field}`] ??
  missingFieldChipLabel[field] ??
  'Missing'

// A CAPCE entry missing only NREMT identifiers still submits to CAPCE — only
// the automatic import into the learner's NREMT account is lost. Allowlisted
// rather than "no license" so any other missing field keeps the blocking copy.
let nremtIdentifierFields = ['nremtNumber', 'emsID']

let isNremtImportOnly = (entry: AccreditationReadinessEntry): boolean =>
  entry.body === 'CAPCE' &&
  entry.missingFields.length > 0 &&
  entry.missingFields.every((field) => nremtIdentifierFields.includes(field))

let nremtImportMessage =
  'Your hours will still be reported to CAPCE. However, they cannot be automatically imported into your NREMT account until the required NREMT profile information is complete.'

let ReadinessComplete = ({
  accreditations,
}: {
  accreditations: AccreditationReadinessEntry[]
}) => {
  let theme = useTheme()
  let borderColor = theme.palette.success.main

  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${alpha(borderColor, 0.3)}`,
        borderTop: `3px solid ${borderColor}`,
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: alpha(borderColor, 0.15),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <CredentialIcon fontSize="small" sx={{ color: 'success.main' }} />
        </Box>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Your hours will be automatically submitted
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Based on your profile, your hours will be reported to the following
            bodies on completion.
          </Typography>
        </Box>
      </Box>

      <Divider />

      <Box sx={{ px: 2.5, py: 1 }}>
        {/* Keyed on the index as well as the body: a course can carry several
            Virginia accreditation rows, so `body` alone is not unique and React
            would collapse them to one row. */}
        {accreditations.map((entry, index) => (
          <Box key={`${entry.body}:${index}`}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <CredentialIcon fontSize="small" color="primary" />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {bodyLabel[entry.body]}
                </Typography>
              </Box>
            </Box>
            {index < accreditations.length - 1 && (
              <Divider sx={{ opacity: 0.4 }} />
            )}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: alpha(borderColor, 0.08),
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <RefreshIcon fontSize="small" sx={{ color: 'success.main' }} />
        <Typography
          variant="caption"
          sx={{ color: 'success.main', fontWeight: 600 }}
        >
          Hours submitted automatically on completion
        </Typography>
      </Box>
    </Box>
  )
}

let ReadinessIncomplete = ({
  accreditations,
}: {
  accreditations: AccreditationReadinessEntry[]
}) => {
  let theme = useTheme()
  let navigate = useNavigate()
  let borderColor = theme.palette.warning.main

  // The entry index rides along because body+field isn't unique either — two
  // Virginia rows can both be missing eligibleLicenseType.
  let allMissingFields = accreditations.flatMap((e, index) =>
    e.missingFields.map((field) => ({ body: e.body, field, index })),
  )

  let capceIncomplete = accreditations.some((e) => e.body === 'CAPCE')
  let capceNremtOnly = accreditations.some(isNremtImportOnly)
  let onlyNremtImport = accreditations.every(isNremtImportOnly)

  let capceMessage = onlyNremtImport
    ? null
    : capceNremtOnly
      ? nremtImportMessage
      : capceIncomplete
        ? 'Without an NREMT number, your hours will not be submitted to CAPCE for the national registry.'
        : null

  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${alpha(borderColor, 0.3)}`,
        borderTop: `3px solid ${borderColor}`,
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: alpha(borderColor, 0.15),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <InfoIcon fontSize="small" sx={{ color: 'warning.main' }} />
        </Box>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {onlyNremtImport
              ? 'Automatic NREMT import is unavailable'
              : 'Your hours will not be automatically submitted'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {onlyNremtImport
              ? nremtImportMessage
              : 'Complete your accreditation profile to ensure CE credits are automatically reported.'}
          </Typography>
        </Box>
      </Box>

      <Divider />

      <Box sx={{ px: 2.5, py: 1 }}>
        {allMissingFields.map(({ body, field, index }) => (
          <Box
            key={`${body}:${index}:${field}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 1.5,
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CredentialIcon fontSize="small" color="disabled" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {resolveMissingFieldLabel(body, field)}
              </Typography>
            </Box>
            <Chip
              label={resolveMissingFieldChipLabel(body, field).toUpperCase()}
              size="small"
              sx={{
                bgcolor: alpha(borderColor, 0.15),
                color: 'warning.main',
                fontWeight: 700,
                fontSize: '0.65rem',
                letterSpacing: 0.5,
                height: 22,
              }}
            />
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          bgcolor: alpha(theme.palette.background.default, 0.5),
          borderTop: `1px solid ${alpha(borderColor, 0.15)}`,
        }}
      >
        {capceMessage != null && (
          <Typography variant="caption" color="text.secondary">
            {capceMessage}
          </Typography>
        )}
        <Button
          variant="contained"
          size="small"
          startIcon={<ProfileIcon />}
          onClick={() => navigate('/profile/licenses')}
          sx={{ flexShrink: 0 }}
        >
          Update Profile
        </Button>
      </Box>
    </Box>
  )
}

let virginiaTopicLabel: Record<string, string> = {
  MEDICAL: 'Medical',
  AIRWAY: 'Airway / Respiration / Ventilation',
  TRAUMA: 'Trauma',
  CARDIO: 'Cardiology',
  OPERATIONS: 'Operations',
}

let providerLevelLabel: Record<VirginiaProviderLevel, string> = {
  als: 'ALS',
  bls: 'BLS',
}

// Category 1 is NCCR credit, keyed to a numbered area on the provider's chart;
// Category 2 is LCCR/ICCR, which Virginia presents as "Approved/Flexible". The
// parenthetical is included because "Category 2" alone reads like a downgrade
// rather than a different kind of credit.
let creditCategoryLabel: Record<VirginiaCreditCategory, string> = {
  category1: 'Category 1 (NCCR)',
  category2: 'Category 2 (Approved)',
}

// A field label above its value. The labels matter here more than in the other
// two cards: 'MEDICAL-04' and '19' are Virginia's own identifiers and mean
// nothing to a learner seeing them bare.
let LabeledValue = ({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant="overline"
      sx={{
        display: 'block',
        color: 'text.secondary',
        fontSize: '0.625rem',
        letterSpacing: 0.8,
        lineHeight: 1.6,
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
)

// One row per Virginia accreditation row on the course. Rendered for EVERY
// Virginia entry regardless of which readiness group it landed in, because the
// category a learner earns is worth knowing whether or not their profile is
// complete enough to auto-submit.
let VirginiaCreditDetail = ({
  entries,
}: {
  entries: AccreditationReadinessEntry[]
}) => {
  let theme = useTheme()
  let borderColor = theme.palette.primary.main

  // Only entries that actually carried a virginia payload. Belt-and-braces: the
  // API sends one on every Virginia entry, but nothing here needs to assume it.
  let rows = entries.flatMap((entry) =>
    entry.virginia ? [entry.virginia] : [],
  )

  if (rows.length === 0) return null

  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${alpha(borderColor, 0.3)}`,
        borderTop: `3px solid ${borderColor}`,
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: alpha(borderColor, 0.15),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <AccreditationIcon fontSize="small" color="primary" />
        </Box>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Virginia CE credit
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {rows.length > 1
              ? 'This course is accredited in more than one Virginia topic area.'
              : 'The topic area and credit category this course counts toward.'}
          </Typography>
        </Box>
      </Box>

      <Divider />

      <Box sx={{ px: 2.5, py: 1 }}>
        {rows.map((virginia, index) => (
          // Keyed on subTopic plus index: subTopic is Virginia's own identifier
          // and should be unique across a course's rows, but the index makes the
          // key correct even if the same topic area is accredited twice.
          <Box key={`${virginia.subTopic}:${index}`}>
            <Box
              sx={{
                py: 1.5,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                columnGap: 3,
                rowGap: 1.5,
              }}
            >
              <LabeledValue label="Topic Area">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {virginiaTopicLabel[virginia.topic] ?? virginia.topic}
                </Typography>
              </LabeledValue>

              <LabeledValue label="Sub Topic">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {virginia.subTopic}
                </Typography>
              </LabeledValue>

              {/* The category shown is the PROVIDER's, never the course's. The
                  course-level value is deliberately optimistic — an ALS-numbered
                  course reports category1 even to a BLS provider who will
                  actually earn category2 — so rendering it would tell a learner
                  they're earning NCCR credit they aren't.

                  No provider means the learner's license doesn't map to either
                  chart — in practice an EMR, which is a real Virginia
                  certification that appears on neither. There is nothing for them
                  to fix and no category we can honestly name, so the whole block
                  is omitted rather than labelled as unknown. */}
              {virginia.provider && (
                <LabeledValue label="Category">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 0.25,
                    }}
                  >
                    <Chip
                      label={creditCategoryLabel[
                        virginia.provider.category
                      ].toUpperCase()}
                      size="small"
                      sx={{
                        bgcolor: alpha(
                          virginia.provider.category === 'category1'
                            ? theme.palette.success.main
                            : theme.palette.info.main,
                          0.15,
                        ),
                        color:
                          virginia.provider.category === 'category1'
                            ? 'success.main'
                            : 'info.main',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        letterSpacing: 0.5,
                        height: 22,
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {providerLevelLabel[virginia.provider.level]}
                      {virginia.provider.areaNumber &&
                        ` · Area ${virginia.provider.areaNumber}`}
                    </Typography>
                  </Box>
                </LabeledValue>
              )}
            </Box>
            {index < rows.length - 1 && <Divider sx={{ opacity: 0.4 }} />}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

type ReadinessGroups = {
  complete: AccreditationReadinessEntry[]
  incomplete: AccreditationReadinessEntry[]
  // Every Virginia entry, in payload order and NOT deduplicated — a course with
  // several Virginia accreditation rows produces several entries, each with its
  // own area numbers and therefore its own category.
  virginia: AccreditationReadinessEntry[]
}

type ReadinessState =
  | { status: 'loading' }
  | { status: 'empty' }
  | { status: 'ready'; groups: ReadinessGroups }

let deriveState = (readiness: AccreditationReadiness): ReadinessState => {
  if (readiness.accreditations.length === 0) return { status: 'empty' }
  let incomplete = readiness.accreditations.filter((a) => !a.willAutoSubmit)
  let complete = readiness.accreditations.filter((a) => a.willAutoSubmit)
  // filter, not find — see AccreditationReadiness. Multiple Virginia entries are
  // normal.
  let virginia = readiness.accreditations.filter((a) => a.body === 'Virginia')
  return { status: 'ready', groups: { incomplete, complete, virginia } }
}

export let AccreditationReadinessCard = ({
  courseID,
}: {
  courseID: string
}) => {
  let [state, setState] = useState<ReadinessState>({ status: 'loading' })

  useEffect(() => {
    fetchAccreditationReadiness(courseID)
      .then((data) => setState(deriveState(data)))
      .catch(() => setState({ status: 'empty' }))
  }, [courseID])

  if (state.status === 'loading') {
    return (
      <Box>
        <span className="sr-only" role="status" aria-live="polite">
          Loading accreditation readiness…
        </span>
        <Skeleton
          variant="rectangular"
          height={160}
          animation="wave"
          sx={{ borderRadius: 2, bgcolor: 'rgba(255,255,255,0.1)' }}
        />
      </Box>
    )
  }

  if (state.status === 'empty') return null

  let { incomplete, complete, virginia } = state.groups

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {incomplete.length > 0 && (
        <ReadinessIncomplete accreditations={incomplete} />
      )}
      {complete.length > 0 && <ReadinessComplete accreditations={complete} />}
      {/* Last, and fed from the ungrouped Virginia entries: the credit category
          is orthogonal to whether the profile is complete enough to auto-submit,
          so it doesn't belong inside either card above. */}
      <VirginiaCreditDetail entries={virginia} />
    </Box>
  )
}
